import { memo } from 'react';
import { useMemo, useState } from 'react';

import { OpportunityFilters } from '@/features/opportunities/OpportunityFilters';
import { OpportunityTable } from '@/features/opportunities/OpportunityTable';
import {
  filterOpportunities,
  initialOpportunityFilters,
  type OpportunityFilterState,
} from '@/features/opportunities/opportunityFilterModel';
import type { EnrichedOpportunity } from '@/models/marketData';
import { exportOpportunitiesToExcel } from '@/utils/exportToExcel';

interface OpportunityRadarProps {
  filters: OpportunityFilterState;
  onFiltersChange: (next: OpportunityFilterState) => void;
  activeBoard: string | null;
  opportunities: EnrichedOpportunity[];
  version: string;
  onSelect: (opportunity: EnrichedOpportunity) => void;
}

export const OpportunityRadar = memo(
  ({
    opportunities,
    version,
    onSelect,
    filters,
    onFiltersChange,
    activeBoard,
  }: OpportunityRadarProps) => {
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const filtered = useMemo(
      () => filterOpportunities(opportunities, filters),
      [filters, opportunities],
    );

    const handleExport = async () => {
      setExporting(true);
      setExportError(null);
      try {
        await exportOpportunitiesToExcel(filtered);
      } catch (error) {
        setExportError(error instanceof Error ? error.message : 'Excel export failed.');
      } finally {
        setExporting(false);
      }
    };

    return (
      <section
        className="panel radar-panel"
        id="opportunity-radar"
        tabIndex={-1}
        aria-label="Opportunity radar"
      >
        <div className="radar-panel__header">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Decision workspace</span>
              <h2>Opportunity radar</h2>
            </div>
            <span>Select a row to explore evidence and illustrative scenarios</span>
          </div>
          {activeBoard && (
            <div className="board-filter-status" role="status">
              <span>
                {activeBoard} ? {filtered.length} stocks
              </span>
              <button type="button" onClick={() => onFiltersChange(initialOpportunityFilters)}>
                Show all stocks
              </button>
            </div>
          )}
          <OpportunityFilters
            filters={filters}
            onChange={onFiltersChange}
            onReset={() => {
              onFiltersChange(initialOpportunityFilters);
              setExportError(null);
            }}
            onExport={() => void handleExport()}
            exporting={exporting}
          />
          {exportError && (
            <p className="inline-error" role="alert">
              {exportError}
            </p>
          )}
        </div>
        <OpportunityTable
          opportunities={filtered}
          totalCount={opportunities.length}
          version={version}
          onSelect={onSelect}
        />
      </section>
    );
  },
);
